// Cursor-following tile info bubble. Shown on tile hover.
//
// Cursor-following positioning means we keep a custom mouse listener instead
// of using shadcn `Tooltip` (which is anchor-based). The styling uses Tailwind
// theme tokens to stay consistent with the rest of the surface.

import type { SceneNode } from '@aod/protocol';
import { playerColor } from '@aod/ui';
import { useEffect, useState } from 'react';
import { useT } from '../i18n/index.js';
import { biomeLabel } from './InfoPanel/biome.js';

interface TileTooltipProps {
  readonly tile: SceneNode | null;
  /** Units standing on the hovered tile — listed under the terrain info. */
  readonly units?: ReadonlyArray<SceneNode>;
}

export function TileTooltip({ tile, units }: TileTooltipProps) {
  const t = useT();
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const onMove = (e: MouseEvent) => setPos({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  if (!tile || !pos) return null;
  const biome = tile.props?.biome as string | undefined;
  const q = tile.props?.q as number | undefined;
  const r = tile.props?.r as number | undefined;
  const resource = tile.props?.resource as string | undefined;
  const depletionPct = tile.props?.resourceDepletionPct as number | undefined;

  return (
    <div
      style={{ left: pos.x + 14, top: pos.y + 14 }}
      className="pointer-events-none fixed z-[200] whitespace-nowrap rounded-md border border-border/70 bg-popover/95 px-2.5 py-1.5 font-mono text-[11px] leading-snug text-popover-foreground shadow-lg backdrop-blur-sm"
    >
      <div className="font-semibold uppercase tracking-[0.12em]">
        {biome ? biomeLabel(t, biome) : '?'}
      </div>
      {resource && (
        <div className="text-amber-700 dark:text-amber-400">
          {resource}
          {depletionPct !== undefined && depletionPct > 0 && (
            <span className="text-muted-foreground"> ({100 - depletionPct}%)</span>
          )}
        </div>
      )}
      <div className="text-muted-foreground">
        q={q ?? '?'} r={r ?? '?'}
      </div>
      {tile && units && units.length > 0 && (
        <div className="mt-1 border-t border-border/50 pt-1">
          {units
            .filter((u) => u.props?.q === tile.props?.q && u.props?.r === tile.props?.r)
            .map((u) => {
              const kind = (u.props?.kind as string) ?? 'unit';
              const hp = (u.props?.hp as number) ?? 0;
              const hpMax = (u.props?.hpMax as number) ?? 0;
              const ownerId = u.props?.ownerId as number | undefined;
              const isHero = Array.isArray(u.tags) && u.tags.includes('hero');
              return (
                <div key={u.id} className="flex items-center gap-1.5">
                  <span
                    aria-hidden
                    className="inline-block size-1.5 rounded-full"
                    style={{ background: playerColor(ownerId) }}
                  />
                  <span>
                    {kind.charAt(0).toUpperCase() + kind.slice(1)}
                    {isHero && ' ★'}
                  </span>
                  <span className="font-mono text-muted-foreground">
                    {hp}/{hpMax}
                  </span>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}
